import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import {
  PlusIcon,
  PencilSquareIcon,
  VideoCameraIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';

const Upload = () => {
  const [title, setTitle] = useState('');
  const [youtubeLink, setYoutubeLink] = useState('');
  const [videos, setVideos] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingLink, setEditingLink] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.email === process.env.REACT_APP_ADMIN_EMAIL) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
        // Opcional: Redirecionar se não for admin e tentar acessar via URL direta
        // navigate('/dashboard/feed'); 
      }
      setLoading(false);
    });

    const fetchVideos = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'videos'));
        const videosList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setVideos(videosList);
      } catch (err) {
        console.error("Erro ao buscar vídeos:", err);
      }
    };

    fetchVideos();
    return () => unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    try {
      if (editingId !== null) {
        const videoRef = doc(db, 'videos', editingId);
        await updateDoc(videoRef, {
          title: editingTitle,
          youtubeLink: editingLink,
        });
        setVideos(videos.map(v => v.id === editingId ? { ...v, title: editingTitle, youtubeLink: editingLink } : v));
        setEditingId(null);
      } else {
        const newVideo = { title, youtubeLink };
        const docRef = await addDoc(collection(db, 'videos'), newVideo);
        setVideos([...videos, { id: docRef.id, ...newVideo }]);
        setTitle('');
        setYoutubeLink('');
      }
    } catch (err) {
      alert("Erro ao salvar. Verifique suas permissões.");
    }
  };

  const handleEdit = (video) => {
    if (!isAdmin) return;
    setEditingId(video.id);
    setEditingTitle(video.title);
    setEditingLink(video.youtubeLink);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (window.confirm("Deseja realmente excluir este vídeo?")) {
      try {
        await deleteDoc(doc(db, 'videos', id));
        setVideos(videos.filter(v => v.id !== id));
      } catch (err) {
        alert("Erro ao excluir vídeo.");
      }
    }
  };

  if (loading) return null;

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
        <ShieldExclamationIcon className="w-16 h-16 text-red-500/50 mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">Acesso Restrito</h2>
        <p className="text-gray-500">Apenas o administrador master pode gerenciar o conteúdo.</p>
        <button
          onClick={() => navigate('/dashboard/feed')}
          className="mt-6 px-6 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-sm font-bold transition-all"
        >
          Voltar para as Aulas
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-10 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Painel da TraderFlix</h1>
          <p className="text-gray-400">Gerenciamento exclusivo do Administrador</p>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-[2.5rem] p-8 backdrop-blur-md">
        <div className="flex items-center gap-3 mb-8">
          <div className={`p-3 rounded-2xl ${editingId ? 'bg-amber-500/20 text-amber-500' : 'bg-emerald-500/20 text-emerald-500'}`}>
            {editingId ? <PencilSquareIcon className="w-6 h-6" /> : <PlusIcon className="w-6 h-6" />}
          </div>
          <h2 className="text-xl font-bold text-white">
            {editingId ? 'Editar Aula: ' + editingTitle : 'Nova Aula'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-4">Título da Aula</label>
            <input
              type="text"
              placeholder="Ex: Aula 05: Price Action"
              className="w-full bg-black/20 border border-white/10 rounded-2xl p-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all font-medium"
              value={editingId !== null ? editingTitle : title}
              onChange={(e) => (editingId !== null ? setEditingTitle(e.target.value) : setTitle(e.target.value))}
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-4">Link do YouTube</label>
            <input
              type="url"
              placeholder="https://..."
              className="w-full bg-black/20 border border-white/10 rounded-2xl p-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all font-medium"
              value={editingId !== null ? editingLink : youtubeLink}
              onChange={(e) => (editingId !== null ? setEditingLink(e.target.value) : setYoutubeLink(e.target.value))}
              required
            />
          </div>
          <div className="md:col-span-2 flex gap-4 mt-2">
            <button
              type="submit"
              className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold transition-all active:scale-95 shadow-lg ${editingId
                ? 'bg-amber-500 hover:bg-amber-400 text-black shadow-amber-500/10'
                : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-600/20'
                }`}
            >
              {editingId ? 'Salvar Edição' : 'Publicar na TraderFlix'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => setEditingId(null)}
                className="px-6 py-4 bg-gray-800 text-gray-400 rounded-2xl font-bold hover:bg-gray-700 transition-all"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="space-y-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-3 px-2">
          <VideoCameraIcon className="w-6 h-6 text-emerald-500" />
          Catálogo Atual
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-center">
          {videos.map((video) => (
            <div key={video.id} className="bg-white/5 border border-white/10 rounded-[2rem] p-6 hover:border-emerald-500/30 transition-all">
              <h3 className="font-bold text-white mb-6 line-clamp-1">{video.title}</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(video)}
                  className="flex-1 py-2 text-xs font-bold bg-white/5 hover:bg-amber-500/20 hover:text-amber-500 rounded-xl transition-all"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(video.id)}
                  className="flex-1 py-2 text-xs font-bold bg-white/5 hover:bg-red-500/20 hover:text-red-500 rounded-xl transition-all"
                >
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Upload;
